import ImageUploadBase from "./shared/ImageUpload";
import { uploadFile } from "../api";
import { useCallback, useMemo, memo } from "react";
import FilteredGalleryImagePreviewModal from "./Interaction/FilteredGalleryImagePreviewModal";

const ImageUpload = ({
  onChange,
  customPathInfo,
  renderGallery,
  showGalleryButton = false,
  galleryFieldName,
  originalImage,
  ...props
}) => {
  const handleUpload = useCallback(
    async (args) => {
      if (Array.isArray(args)) {
        // Multiple items: may be a mix of files to upload and already-selected URLs
        const uploads = await Promise.all(
          args.map(async (item) => {
            if (!item) return null;
            if (item.url && !item.body) {
              return {
                name: item.name,
                source: item.source || "upload",
                url: item.url,
              };
            }
            if (!item.body) return null;
            const { url } = await uploadFile({ ...item, customPathInfo });
            return { name: item.name, source: item.source || "upload", url };
          })
        );
        const results = uploads.filter(Boolean);
        if (typeof onChange === "function") {
          onChange(results);
        }
        return;
      }

      // Single item can be an uploaded file or a preselected URL from gallery
      if (args?.url && !args?.body) {
        if (typeof onChange === "function") {
          onChange({
            name: args.name,
            source: args.source || "upload",
            url: args.url,
          });
        }
        return;
      }

      if (!args?.body) {
        if (typeof onChange === "function") {
          onChange({ url: "" });
        }
        return;
      }
      const { url } = await uploadFile({
        ...args,
        customPathInfo,
      });
      if (typeof onChange === "function") {
        onChange({
          name: args.name,
          source: args.source || "upload",
          url,
        });
      }
    },
    [onChange, customPathInfo]
  );

  // Memoize the default renderGallery function to prevent unnecessary re-renders
  // and network requests when the modal remounts
  const defaultRenderGallery = useMemo(
    () =>
      renderGallery ||
      (({ open, onClose, onSelect }) =>
        open ? (
          <FilteredGalleryImagePreviewModal
            key={galleryFieldName || "default"}
            fieldName={galleryFieldName}
            multipleImages={false}
            onlyPublished={true}
            onClose={onClose}
            onConfirm={onSelect}
          />
        ) : null),
    [renderGallery, galleryFieldName]
  );

  return (
    <ImageUploadBase
      {...props}
      originalImage={originalImage}
      renderGallery={defaultRenderGallery}
      showGalleryButton={showGalleryButton}
      onChange={handleUpload}
    />
  );
};

export default memo(ImageUpload);
